import { useId } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  TIMING_LIMITS,
  clampSetting,
  formatElapsed,
  formatSeconds,
  isOutOfRange,
  ticketLifeRows,
  type KitchenFormState,
  type TicketLife,
} from './ticketLife'

/**
 * The stages a ticket passes through on the Kitchen Display, in the colours
 * the cards themselves use. `startsAt` picks the moment each stage begins.
 */
const STAGES: {
  key: string
  label: string
  headClass: string
  startsAt: (life: TicketLife) => number
}[] = [
  {
    key: 'calm',
    label: 'Calm',
    headClass: 'border-success-border bg-success-surface text-success-fg',
    startsAt: () => 0,
  },
  {
    key: 'amber',
    label: 'Amber',
    headClass: 'border-warning-border bg-warning-surface text-warning-fg',
    startsAt: (l) => l.amberAt,
  },
  {
    key: 'red',
    label: 'Red',
    headClass: 'border-danger-border bg-danger-surface text-danger-fg',
    startsAt: (l) => l.redAt,
  },
  {
    key: 'flashing',
    label: 'Flashing red',
    headClass: 'border-danger-solid bg-danger-solid text-danger-solid-fg',
    startsAt: (l) => l.flashingAt,
  },
  {
    key: 'hidden',
    label: 'Off the screen',
    headClass: 'border-dashed border-muted-foreground/50 bg-muted text-muted-foreground',
    startsAt: (l) => l.hiddenAt,
  },
]

function UnitField({
  label,
  unit,
  hint,
  error,
  value,
  onChange,
  placeholder,
  min,
  max,
}: {
  label: string
  unit: string
  hint?: string
  error?: string
  value: string
  onChange: (next: string) => void
  placeholder?: string
  min: number
  max: number
}) {
  const id = useId()
  const hintId = `${id}-hint`
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? hintId : undefined}
          className={cn(
            'pr-12 tabular-nums',
            error && 'border-danger-solid focus-visible:ring-danger-solid',
          )}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
          {unit}
        </span>
      </div>
      {(error || hint) && (
        <p
          id={hintId}
          className={cn('mt-1.5 text-xs', error ? 'text-danger-fg' : 'text-muted-foreground')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  )
}

function rangeError(raw: string, limit: { min: number; max: number; fallback: number }, unit: string) {
  if (!isOutOfRange(raw, limit)) return undefined
  return `Enter ${limit.min} to ${limit.max}. Saving now would store ${clampSetting(raw, limit)} ${unit}.`
}

export function TicketTimingPanel({
  form,
  onChange,
}: {
  form: KitchenFormState
  onChange: (patch: Partial<KitchenFormState>) => void
}) {
  const rows = ticketLifeRows(form)
  const target = clampSetting(form.urgencyMinutes, TIMING_LIMITS.target)
  const recall = clampSetting(form.recallWindowSeconds, TIMING_LIMITS.recall)
  const overrideError = (raw: string) =>
    raw.trim() !== '' && isOutOfRange(raw, TIMING_LIMITS.target)
      ? `Enter ${TIMING_LIMITS.target.min} to ${TIMING_LIMITS.target.max}, or leave blank.`
      : undefined

  return (
    <section aria-labelledby="ticket-timing-heading" className="space-y-4">
      <div>
        <h3 id="ticket-timing-heading" className="text-lg font-semibold tracking-tight">
          Ticket timing
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Set how long the kitchen should take. The display changes a ticket&rsquo;s colour as that
          time runs out.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="overflow-x-auto px-4 pt-4 sm:px-5 sm:pt-5">
          <table className="w-full min-w-[40rem] table-fixed border-separate border-spacing-0 whitespace-nowrap text-sm">
            <caption className="sr-only">
              Time after an order is fired at which its ticket changes on the Kitchen Display
            </caption>
            <thead>
              <tr>
                <th scope="col" className="w-32 pb-2 pr-3 text-left font-normal text-muted-foreground">
                  Ticket turns
                </th>
                {STAGES.map((stage, i) => (
                  <th
                    key={stage.key}
                    scope="col"
                    className={cn(
                      'border-y border-l px-3 py-2 text-left text-xs font-semibold',
                      i === 0 && 'rounded-l-md',
                      i === STAGES.length - 1 && 'rounded-r-md border-r',
                      stage.headClass,
                    )}
                  >
                    {stage.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th scope="row" className="py-2 pr-3 text-left font-medium">
                    {row.label}
                  </th>
                  {STAGES.map((stage) => (
                    <td key={stage.key} className="px-3 py-2 tabular-nums">
                      {stage.key === 'calm' ? (
                        <span className="text-muted-foreground">from 0:00</span>
                      ) : (
                        formatElapsed(stage.startsAt(row.life))
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 pb-4 pt-1 text-xs text-muted-foreground sm:px-5">
          Minutes and seconds since the order was fired.
        </p>

        <div className="grid gap-5 border-t border-border p-4 sm:grid-cols-3 sm:p-5">
          <UnitField
            label="Target time"
            unit="min"
            min={TIMING_LIMITS.target.min}
            max={TIMING_LIMITS.target.max}
            value={form.urgencyMinutes}
            onChange={(urgencyMinutes) => onChange({ urgencyMinutes })}
            error={rangeError(form.urgencyMinutes, TIMING_LIMITS.target, 'min')}
            hint="How long an order should take. The colours follow from it."
          />
          <UnitField
            label="Take off the screen after"
            unit="min"
            min={TIMING_LIMITS.stale.min}
            max={TIMING_LIMITS.stale.max}
            value={form.staleMinutes}
            onChange={(staleMinutes) => onChange({ staleMinutes })}
            error={rangeError(form.staleMinutes, TIMING_LIMITS.stale, 'min')}
            hint="Clears forgotten tickets from the default view."
          />
          <UnitField
            label="Undo a bump within"
            unit="sec"
            min={TIMING_LIMITS.recall.min}
            max={TIMING_LIMITS.recall.max}
            value={form.recallWindowSeconds}
            onChange={(recallWindowSeconds) => onChange({ recallWindowSeconds })}
            error={rangeError(form.recallWindowSeconds, TIMING_LIMITS.recall, 'sec')}
            hint={
              form.recallWindowSeconds.trim() === '0'
                ? 'Undo is off. A bumped ticket cannot be brought back.'
                : `Cooks have ${formatSeconds(recall)} to bring a ticket back.`
            }
          />
        </div>

        <fieldset className="border-t border-border p-4 sm:p-5">
          <legend className="float-left mb-1 w-full text-sm font-medium">
            A different target for some orders
          </legend>
          <p className="clear-both text-sm text-muted-foreground">
            Give delivery longer than dine-in, for example. Blank uses the {target} min target.
          </p>
          <div className="mt-4 grid gap-5 sm:grid-cols-3">
            <UnitField
              label="Dine-in"
              unit="min"
              min={TIMING_LIMITS.target.min}
              max={TIMING_LIMITS.target.max}
              placeholder={String(target)}
              value={form.prepTargetDineIn}
              onChange={(prepTargetDineIn) => onChange({ prepTargetDineIn })}
              error={overrideError(form.prepTargetDineIn)}
            />
            <UnitField
              label="Takeaway"
              unit="min"
              min={TIMING_LIMITS.target.min}
              max={TIMING_LIMITS.target.max}
              placeholder={String(target)}
              value={form.prepTargetTakeout}
              onChange={(prepTargetTakeout) => onChange({ prepTargetTakeout })}
              error={overrideError(form.prepTargetTakeout)}
            />
            <UnitField
              label="Delivery"
              unit="min"
              min={TIMING_LIMITS.target.min}
              max={TIMING_LIMITS.target.max}
              placeholder={String(target)}
              value={form.prepTargetDelivery}
              onChange={(prepTargetDelivery) => onChange({ prepTargetDelivery })}
              error={overrideError(form.prepTargetDelivery)}
            />
          </div>
        </fieldset>
      </div>
    </section>
  )
}
